import styles from "./table.module.css";
import { ReactNode } from "react";
import { Button, TimelineEvent } from "@narmi/design_system";

import { TaggedNoteEvent, CaseNote, CaseEvent, InformationRequest } from "./CaseDetailDrawer";
import { Case } from "./CaseTable";
import { Payment } from "./PaymentTable";
import { dateOptions } from "./PaymentDetailDrawer";
import {
  ACWP_REASONS,
  ACWP_REASONS_TYPE,
  CANCELLATION_CODES,
  CANCELLATION_CODES_TYPE,
  RETURN_ACCEPT_REASONS,
  RETURN_ACCEPT_REASONS_TYPE,
  RETURN_REJECT_REASONS,
  RETURN_REJECT_REASONS_TYPE,
} from "./codesets";
import { formatInformationRequest } from "./FormatInformationRequest";
import { useInstitutionContext } from "@hooks/institutionContext.tsx";

type RowType = "note" | "event" | "information_request" | "no_additional";

export function HistoryRow({
  content,
  author,
  additional,
  timestamp,
  kind,
  rowType,
  onClickOverride,
}: {
  content: string;
  author?: string;
  additional?: string | ReactNode;
  timestamp?: string;
  kind: "pending" | "start" | "node";
  rowType: RowType;
  onClickOverride?: CallableFunction;
}) {
  return (
    <TimelineEvent
      tooltip={author}
      icon={author ? "user" : undefined}
      kind={kind}
      key={`${kind}-${content}`}
    >
      <div style={{ width: "100%", display: "inline-grid", gridTemplateColumns: "auto auto" }}>
        <p
          className={`${styles.truncateOverflow} margin--y--none ${
            kind === "pending" ? styles.lightText : ""
          }`}
        >
          {content}
        </p>
        <div className={styles.fixedWidthRowItem}>
          <p>{timestamp ? new Date(timestamp).toLocaleString("en-US", dateOptions) : ""}</p>
        </div>
      </div>
      {kind !== "pending" && rowType !== "no_additional" && (
        <>
          {onClickOverride ? (
            <Button
              label={
                rowType === "information_request" ? "View information request" : `View ${rowType}`
              }
              kind="plain"
              onClick={() => onClickOverride()}
            />
          ) : additional ? (
            <div className="fontSize--s fontColor--secondary padding--top--xs">{additional}</div>
          ) : null}
        </>
      )}
    </TimelineEvent>
  );
}

export default function CaseHistoryList({
  history,
  caseobj,
  payment,
  informationRequests,
}: {
  history: Array<TaggedNoteEvent>;
  caseobj: Case;
  payment: Payment;
  informationRequests: Map<string, InformationRequest>;
}) {
  const { institution } = useInstitutionContext();
  const longName = institution.long_name || "";
  const showAwaitingResponse =
    caseobj.status === "open" && (caseobj.deadline || caseobj.kind === "ACWP");

  return (
    <>
      {showAwaitingResponse && (
        <HistoryRow content="Awaiting response" kind="pending" rowType="event" />
      )}

      {history.map((item: TaggedNoteEvent, index: number) => {
        let content = "";
        let additional;
        let rowType: RowType = "note";
        if (item.type === "note") {
          const noteText = (item.content as CaseNote).text;
          content = noteText.length > 120 ? `${noteText.slice(0, 117)}...` : noteText;
          additional = noteText.length > 120 ? noteText : undefined;
        } else {
          const event: CaseEvent = item.content as CaseEvent;
          rowType = "event";
          if (event.type === "created") {
            const originatingFi =
              caseobj.kind === "ACWP" && payment.type === "receive"
                ? longName
                : payment.source_fi_name;
            content = `Case opened by ${item.content.author || originatingFi}.`;
            if (caseobj.kind === "ACWP") {
              additional = event.metadata?.reason
                ? ACWP_REASONS[event.metadata.reason as ACWP_REASONS_TYPE]
                : content;
            } else if (caseobj.kind === "return") {
              additional = event.metadata?.reason
                ? CANCELLATION_CODES[event.metadata.reason as CANCELLATION_CODES_TYPE]
                : content;
            }
          } else if (event.type === "resolved") {
            const closingFi =
              caseobj.kind === "ACWP" && payment.type === "receive"
                ? longName
                : payment.source_fi_name;
            content = `Case closed by ${event.author || closingFi}.`;
            additional =
              RETURN_ACCEPT_REASONS[
                event.metadata.resolution_reason as RETURN_ACCEPT_REASONS_TYPE
              ] ||
              RETURN_REJECT_REASONS[event.metadata.reject_reason as RETURN_REJECT_REASONS_TYPE];
          } else if (event.type === "ir_created") {
            content = `Information requested by ${event.author}`;
            const information_request = informationRequests.get(
              event.metadata.information_request_identification!
            );
            additional =
              information_request !== undefined
                ? formatInformationRequest(information_request)
                : content;
            rowType = "information_request";
          } else if (event.type === "ir_answered") {
            content = "Information request answered";
            const information_request = informationRequests.get(
              event.metadata.information_request_identification!
            );
            additional =
              information_request !== undefined
                ? formatInformationRequest(information_request)
                : content;
            rowType = "information_request";
          } else if (event.type === "return_failed") {
            content = "Return attempt failed";
            rowType = "no_additional";
          } else {
            content = event.metadata?.reason || "Case updated";
            additional = event.metadata?.reason;
          }
          if (event.metadata?.additional_info) {
            additional = (
              <>
                <p>{additional}</p>
                <p>{event.metadata?.additional_info}</p>
              </>
            );
          }
        }

        return (
          <HistoryRow
            key={`${item.type}-${item.content.created_at}`}
            content={content}
            additional={additional}
            author={item.content.author}
            timestamp={item.content.created_at}
            kind={index === history.length - 1 ? "start" : "node"}
            rowType={rowType}
          />
        );
      })}
    </>
  );
}
